import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, cn } from "../../shared/ui";
import type { CalendarDetail } from "../../shared/types/api";

const DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
] as const;

/** A sensible starting pattern: Monday to Friday, eight hours. */
const DEFAULT_PATTERN: Record<string, number> = {
  MONDAY: 480,
  TUESDAY: 480,
  WEDNESDAY: 480,
  THURSDAY: 480,
  FRIDAY: 480,
  SATURDAY: 0,
  SUNDAY: 0,
};

function minutesToHours(minutes: number): string {
  return minutes === 0 ? "" : String(minutes / 60);
}

function hoursToMinutes(value: string): number {
  const hours = Number(value);
  return Number.isFinite(hours) && hours > 0 ? Math.round(hours * 60) : 0;
}

/**
 * Creates or edits a working calendar (§37).
 *
 * <p>Hours are entered and minutes are stored. People think in hours, and a field asking for 480
 * would be answered wrongly more often than not; the conversion happens here so the database keeps
 * the exact integer arithmetic every capacity sum depends on.
 *
 * <p>A day left blank is a non-working day, which is what makes a four-day week or a half-day
 * Saturday expressible without a special case.
 */
export function CalendarDrawer({
  calendar,
  open,
  onClose,
  onSaved,
}: {
  /** The calendar being edited, or null to create a new one. */
  calendar: CalendarDetail | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = Boolean(calendar);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [timeZone, setTimeZone] = useState("Asia/Kolkata");
  const [active, setActive] = useState(true);
  const [hours, setHours] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const source = calendar?.workingMinutesByDay ?? DEFAULT_PATTERN;
    setCode(calendar?.code ?? "");
    setName(calendar?.name ?? "");
    setTimeZone(calendar?.timeZone ?? "Asia/Kolkata");
    setActive(calendar?.active ?? true);
    setHours(
      Object.fromEntries(DAYS.map((day) => [day, minutesToHours(source[day] ?? 0)])),
    );
    setError(null);
  }, [calendar, open]);

  const workingMinutesByDay = Object.fromEntries(
    DAYS.map((day) => [day, hoursToMinutes(hours[day] ?? "")]),
  );
  const weeklyMinutes = Object.values(workingMinutesByDay).reduce((sum, m) => sum + m, 0);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isEdit && calendar) {
        await api.patch(`/api/v1/working-calendars/${calendar.id}`, {
          name,
          timeZone,
          workingMinutesByDay,
          active,
          version: calendar.version,
        });
      } else {
        await api.post("/api/v1/working-calendars", {
          code,
          name,
          timeZone,
          workingMinutesByDay,
        });
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.code === "DUPLICATE_RESOURCE"
          ? "A calendar with that code already exists."
          : caught instanceof ApiError
            ? caught.message
            : "Could not save the calendar.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit calendar" : "New working calendar"}
      subtitle={
        isEdit
          ? calendar?.code
          : "Defines which days are worked and for how long."
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={name.trim() === "" || (!isEdit && code.trim() === "") || weeklyMinutes === 0}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create calendar"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {!isEdit && (
          <Field label="Code" required hint="A short identifier, e.g. PART_TIME">
            <Input
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="PART_TIME"
            />
          </Field>
        )}

        <Field label="Name" required>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Four-day week"
          />
        </Field>

        <Field label="Time zone" required hint="An IANA zone, e.g. Asia/Kolkata or Europe/London">
          <Input value={timeZone} onChange={(event) => setTimeZone(event.target.value)} />
        </Field>

        <div>
          <p className="mb-1 text-sm font-medium text-ink-700">
            Working hours per day <span className="text-red-500">*</span>
          </p>
          <p className="mb-2 text-xs text-ink-400">
            Leave a day blank to make it a non-working day.
          </p>
          <div className="overflow-hidden rounded-lg border border-ink-200">
            {DAYS.map((day) => (
              <div
                key={day}
                className={cn(
                  "flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 last:border-0",
                  workingMinutesByDay[day] === 0 && "bg-ink-50/60",
                )}
              >
                <span className="text-sm capitalize text-ink-700">{day.toLowerCase()}</span>
                <div className="w-24">
                  <Input
                    type="number"
                    min={0}
                    max={24}
                    step={0.5}
                    placeholder="—"
                    className="text-right"
                    value={hours[day] ?? ""}
                    onChange={(event) =>
                      setHours((current) => ({ ...current, [day]: event.target.value }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-sm text-ink-500">
            <span className="tnum font-medium text-ink-900">{weeklyMinutes / 60}h</span> a week
          </p>
          {weeklyMinutes === 0 && (
            <p className="mt-1 text-xs text-red-600">
              A calendar must have at least one working day, or everyone on it has zero expected
              hours.
            </p>
          )}
        </div>

        {isEdit && (
          <Field
            label="Active"
            hint={
              calendar?.defaultCalendar
                ? "The default calendar cannot be deactivated — new employees fall back to it."
                : "Inactive calendars are hidden when choosing one for an employee."
            }
          >
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={active}
                disabled={calendar?.defaultCalendar}
                onChange={(event) => setActive(event.target.checked)}
                className="h-4 w-4 accent-brand-600"
              />
              In use
            </label>
          </Field>
        )}
      </div>
    </Drawer>
  );
}
